/** Shared, design-matched UI primitives (light glass theme). */

import { useMemo, type CSSProperties, type ReactNode } from "react";
import { Icons } from "./icons";

/* ---------- type helpers ---------- */
export function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="eyebrow" style={style}>
      {children}
    </div>
  );
}

export function Spinner({ size = 16, style }: { size?: number; style?: CSSProperties }) {
  return <Icons.spark size={size} className="spin" style={style} />;
}

export function Skeleton({ w = 90, h = 22 }: { w?: number | string; h?: number }) {
  return (
    <div
      style={{
        width: w,
        height: h,
        borderRadius: 8,
        background: "var(--bg-3)",
        opacity: 0.7,
        animation: "fadeIn 0.4s var(--smooth)",
      }}
    />
  );
}

/* ---------- Badge ---------- */
const BADGE_MAP: Record<string, [string, string, string]> = {
  deployed: ["rgba(208,181,111,0.30)", "rgba(208,181,111,0.10)", "#9A7726"],
  live: ["rgba(21,160,90,0.32)", "rgba(21,160,90,0.10)", "#127A46"],
  armed: ["rgba(217,119,6,0.32)", "rgba(217,119,6,0.10)", "#B45F09"],
  pos: ["rgba(14,159,110,0.30)", "rgba(14,159,110,0.10)", "#0E8B62"],
  neg: ["rgba(225,29,72,0.30)", "rgba(225,29,72,0.10)", "#C81644"],
  accent: ["rgba(var(--accent-rgb),0.30)", "rgba(var(--accent-rgb),0.10)", "var(--accent)"],
  neutral: ["var(--border)", "var(--surface-alt)", "var(--text-soft)"],
};

export function Badge({
  variant = "neutral",
  children,
}: {
  variant?: keyof typeof BADGE_MAP;
  children: ReactNode;
}) {
  const [bd, bg, tx] = BADGE_MAP[variant] || BADGE_MAP.neutral;
  return (
    <span className="badge" style={{ borderColor: bd, background: bg, color: tx }}>
      {children}
    </span>
  );
}

/* ---------- Sparkline ---------- */
export function Sparkline({
  data,
  color = "var(--accent)",
  w = 120,
  h = 36,
  fill = true,
  sw = 1.8,
}: {
  data: number[];
  color?: string;
  w?: number;
  h?: number;
  fill?: boolean;
  sw?: number;
}) {
  const { min, max } = useMemo(
    () => ({ min: Math.min(...data), max: Math.max(...data) }),
    [data],
  );
  const span = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - ((v - min) / span) * (h - 4) - 2,
  ]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${w} ${h} L0 ${h} Z`;
  const gid = "sg" + useMemo(() => Math.random().toString(36).slice(2, 8), []);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", overflow: "visible" }}>
      {fill && (
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity="0.22" />
            <stop offset="1" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {fill && <path d={area} fill={`url(#${gid})`} />}
      <path d={line} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- Donut ---------- */
export function Donut({
  slices,
  size = 168,
  thickness = 22,
}: {
  slices: { value: number; color: string }[];
  size?: number;
  thickness?: number;
}) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const C = 2 * Math.PI * r;
  let off = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {slices.map((s, i) => {
          const frac = s.value / total;
          const dash = frac * C;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${C - dash}`}
              strokeDashoffset={-off}
              strokeLinecap="butt"
              style={{ transition: "stroke-dashoffset 0.6s var(--smooth)" }}
            />
          );
          off += dash;
          return el;
        })}
      </g>
    </svg>
  );
}

/* ---------- StatTile ---------- */
export function StatTile({
  label,
  value,
  sub,
  trend,
  spark,
  color = "var(--accent)",
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  trend?: number | null;
  spark?: number[];
  color?: string;
}) {
  return (
    <div className="card" style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div className="eyebrow">{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <div className="serif tnum" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
          {value}
        </div>
        {trend != null && (
          <span
            className="mono tnum"
            style={{
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: trend >= 0 ? "var(--pos)" : "var(--neg)",
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            {trend >= 0 ? "▲" : "▼"} {Math.abs(trend).toFixed(2)}%
          </span>
        )}
      </div>
      {spark ? (
        <div style={{ marginTop: 4 }}>
          <Sparkline data={spark} color={color} w={220} h={34} />
        </div>
      ) : sub ? (
        <div style={{ fontSize: 12.5, color: "var(--micro)" }}>{sub}</div>
      ) : null}
    </div>
  );
}

/* ---------- Row (label / value) ---------- */
export function Row({
  k,
  v,
  sub,
  strong,
}: {
  k: ReactNode;
  v: ReactNode;
  sub?: string;
  strong?: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14 }}>
      <span style={{ fontSize: 13, color: "var(--text-soft)", whiteSpace: "nowrap" }}>{k}</span>
      <span style={{ textAlign: "right" }}>
        <span
          className="mono tnum"
          style={{ fontSize: strong ? 15 : 13, fontWeight: strong ? 700 : 600, color: "var(--text)", whiteSpace: "nowrap" }}
        >
          {v}
        </span>
        {sub && <div style={{ fontSize: 10.5, color: "var(--micro)" }}>{sub}</div>}
      </span>
    </div>
  );
}

/* ---------- PageHead / SectionTitle ---------- */
export function PageHead({
  eyebrow,
  title,
  sub,
  actions,
}: {
  eyebrow?: string;
  title: ReactNode;
  sub?: string;
  actions?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 26 }}>
      <div>
        {eyebrow && <div className="eyebrow" style={{ marginBottom: 8 }}>{eyebrow}</div>}
        <h1 className="serif" style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.01em", margin: 0 }}>
          {title}
        </h1>
        {sub && <p style={{ fontSize: 14.5, color: "var(--text-soft)", margin: "8px 0 0", maxWidth: 560 }}>{sub}</p>}
      </div>
      {actions}
    </div>
  );
}

export function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <div
        className="mono"
        style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-soft)", fontWeight: 600, whiteSpace: "nowrap" }}
      >
        {children}
      </div>
      {right}
    </div>
  );
}

/* ---------- Callout (status banner) ---------- */
export function Callout({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "info" | "warn" | "error" | "success";
  children: ReactNode;
}) {
  const map: Record<string, [string, string, string]> = {
    neutral: ["var(--border)", "var(--surface-alt)", "var(--text-soft)"],
    info: ["rgba(var(--accent-rgb),0.25)", "var(--accent-soft)", "var(--accent)"],
    warn: ["rgba(217,119,6,0.3)", "rgba(217,119,6,0.08)", "#B45F09"],
    error: ["rgba(225,29,72,0.3)", "rgba(225,29,72,0.07)", "#C81644"],
    success: ["rgba(21,160,90,0.3)", "rgba(21,160,90,0.08)", "#127A46"],
  };
  const [bd, bg, tx] = map[tone];
  return (
    <div style={{ border: `1px solid ${bd}`, background: bg, color: tx, borderRadius: 12, padding: "11px 14px", fontSize: 13, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}
